import  React from "react";
import type { FC } from "react";

import styled from "@emotion/styled";

import { useIntl } from "react-intl";

import { useRecoilCallback, useRecoilValue } from "recoil";

import {
	BUTTON_VARIANT_PRIMARY,
	BUTTON_VARIANT_SECONDARY,
	Button,
	CHECKBOX_LABEL_VARIANT_BOLD,
	Checkbox,
	Flex,
	MODAL_SIZE_MD,
	MODAL_SIZE_SM,
	Modal,
	ModalBody,
	ModalFoot,
	ModalHead,
	SPACING_4,
	SpacingVertical,
	TEXT_VARIANT_LIGHT,
	TEXT_VARIANT_STANDARD,
	Text,
	Toggle,
} from "mailup-web-components";

import Divider from "Components/Divider";

import {
	modalBulkUnsubscribeCancelCallback,
	modalBulkUnsubscribeConfirmCallback,
	modalBulkUnsubscribeToggleFieldCallback,
} from "Pages/RecipientsList/recoil/callbacks/modalBulkUnsubscribe";
import {
	modalBulkUnsubscribeCanUnsubscribeState,
	modalBulkUnsubscribeCanWipeDataState,
	modalBulkUnsubscribeDisableWipeDataState,
	modalBulkUnsubscribeState,
} from "Pages/RecipientsList/recoil/state/modals";

import { selectionCountState } from "Pages/RecipientsList/recoil/state/results";
import { gainsightTrack } from "mailup-react-helpers";
const StyledToggleLabel = styled.label`
  cursor: pointer;
`;

const ModalBulkUnsubscribe: FC = () => {
	const intl = useIntl();

	const { show, channelEmail, channelSMS, wipeData } = useRecoilValue(
		modalBulkUnsubscribeState,
	);

	const canUnsubscribe = useRecoilValue(
		modalBulkUnsubscribeCanUnsubscribeState,
	);

	const count = useRecoilValue(selectionCountState);

	const canWipeData: boolean = useRecoilValue(
		modalBulkUnsubscribeCanWipeDataState,
	);

	const disableWipeData = useRecoilValue(
		modalBulkUnsubscribeDisableWipeDataState,
	);

	const handleCancel = useRecoilCallback(
		modalBulkUnsubscribeCancelCallback,
		[],
	);
	const handleConfirm = useRecoilCallback(
		modalBulkUnsubscribeConfirmCallback,
		[],
	);

	const modalUnsubscribeToggleField = useRecoilCallback(
		modalBulkUnsubscribeToggleFieldCallback,
		[],
	);

	const handleToggleChannelEmail = (
		_evt: React.ChangeEvent<HTMLInputElement>,
	): void => {
		void modalUnsubscribeToggleField("channelEmail");
	};

	const handleToggleChannelSMS = (
		_evt: React.ChangeEvent<HTMLInputElement>,
	): void => {
		gainsightTrack(
			"mup_recipients_click_modalUnsubscribeSmsChannelChecked",
			{},
		);

		void modalUnsubscribeToggleField("channelSMS");
	};

	const handleToggleWipeData = (
		_evt: React.ChangeEvent<HTMLInputElement>,
	): void => {
		gainsightTrack("mup_recipients_click_modalUnsubscribeOblivionToggle", {});
		void modalUnsubscribeToggleField("wipeData");
	};

	return (
		<Modal
			size={canWipeData ? MODAL_SIZE_MD : MODAL_SIZE_SM}
			show={show}
			onClose={handleCancel}
			data-mu-test="modal-bulk-unsubscribe"
			closeOnOverlayClick
			showClose
		>
			<ModalHead
				title={intl.formatMessage({
					id: "Load.RecipientsList.ModalBulkUnsubscribeTitle",
				})}
			/>

			<ModalBody>
				<Flex flexDirection="column" gap="24px">
					<Text variant={TEXT_VARIANT_STANDARD}>
						{intl.formatMessage({
							id: "Load.RecipientsList.ModalUnsubscribeIntro",
						})}
					</Text>

					<Flex flexDirection="column" gap="16px">
						<Checkbox
							value="email"
							checked={channelEmail}
							onChange={handleToggleChannelEmail}
							label={intl.formatMessage({
								id: "Load.RecipientsList.ModalUnsubscribeChannelEmail",
							})}
							labelVariant={CHECKBOX_LABEL_VARIANT_BOLD}
							data-mu-test="channel-email"
						/>
						<Checkbox
							value="sms"
							checked={channelSMS}
							onChange={handleToggleChannelSMS}
							label={intl.formatMessage({
								id: "Load.RecipientsList.ModalUnsubscribeChannelSMS",
							})}
							labelVariant={CHECKBOX_LABEL_VARIANT_BOLD}
							data-mu-test="channel-sms"
						/>
					</Flex>

					{canWipeData && (
						<>
							<Divider margin="0" />

							<section>
								<Flex flexDirection="row" alignItems="center" gap="8px">
									<Toggle
										data-mu-test="toggle-wipe-data"
										variant="sm"
										checked={wipeData}
										onChange={handleToggleWipeData}
										disabled={disableWipeData}
										id="toggle-wipe-data"
									/>

									<StyledToggleLabel htmlFor="toggle-wipe-data">
										{intl.formatMessage({
											id: "Load.RecipientsList.ModalUnsubscribeWipeData",
										})}
									</StyledToggleLabel>
								</Flex>

								<SpacingVertical variant={SPACING_4} />

								<Flex flexDirection="row" gap="45px">
									<i />
									<Text variant={TEXT_VARIANT_LIGHT}>
										{intl.formatMessage({
											id: "Load.RecipientsList.ModalUnsubscribeWipeDataDescription",
										})}
									</Text>
								</Flex>
							</section>

							<Divider margin="0" />
						</>
					)}
				</Flex>
			</ModalBody>
			<ModalFoot>
				<Button
					kind={BUTTON_VARIANT_SECONDARY}
					data-mu-test="cancel"
					onClick={handleCancel}
				>
					{intl.formatMessage({ id: "Load.MailupCommon.Cancel" })}
				</Button>
				<Button
					kind={BUTTON_VARIANT_PRIMARY}
					data-mu-test="confirm"
					onClick={handleConfirm}
					disabled={!canUnsubscribe}
				>
					{intl.formatMessage(
						{ id: "Load.RecipientsList.ActionBulkUnsubscribe" },
						{
							count,
						},
					)}
				</Button>
			</ModalFoot>
		</Modal>
	);
};

export default ModalBulkUnsubscribe;
